// libraries
// react
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';

// internationalization
import type { TFunction } from 'i18next';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';

// material ui
import {
  Autocomplete,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import type { LocationDto } from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
// shared-components
import { RHFTextField } from '@/components/ui/form';

// relative
import {
  getDaluxBuildingLabel,
  getRemoniLocationLabel,
} from '../daluxTicketUtils';
import type {
  DaluxBuildingOption,
  DaluxTicketFormValues,
  DaluxTopicOption,
} from './daluxTicketForm.schema';

type DaluxTicketFieldsProps = {
  control: Control<DaluxTicketFormValues>;
  t: TFunction;
  disabled?: boolean;
  topics?: DaluxTopicOption[];
  topicsLoading?: boolean;
  buildingOptions?: DaluxBuildingOption[];
  buildingsLoading?: boolean;
  energyGroupName?: string | null;
  remoniLocation?: LocationDto;
  showNoMatchingBuildingsWarning?: boolean;
  showPhotoUpload?: boolean;
};

export default function DaluxTicketFields({
  control,
  t,
  disabled = false,
  topics = [],
  topicsLoading = false,
  buildingOptions = [],
  buildingsLoading = false,
  energyGroupName,
  remoniLocation,
  showNoMatchingBuildingsWarning = false,
  showPhotoUpload = false,
}: DaluxTicketFieldsProps) {
  return (
    <Stack spacing={2}>
      {energyGroupName ? (
        <Typography variant="body2" color="text.secondary">
          {t('ticket.deepdive_label')}: <strong>{energyGroupName}</strong>
        </Typography>
      ) : null}
      {remoniLocation ? (
        <Typography variant="body2" color="text.secondary">
          {t('ticket.remoni_location_label')}:{' '}
          <strong>{getRemoniLocationLabel(remoniLocation)}</strong>
        </Typography>
      ) : null}
      {showNoMatchingBuildingsWarning ? (
        <Typography variant="body2" color="warning.main">
          {t('ticket.no_matching_buildings')}
        </Typography>
      ) : null}

      <Controller
        name="buildingId"
        control={control}
        render={({ field, fieldState }) => (
          <Autocomplete
            fullWidth
            options={buildingOptions}
            loading={buildingsLoading}
            disabled={disabled}
            value={
              buildingOptions.find(
                (option) => option?.data?.buildingId === field.value,
              ) || null
            }
            onChange={(_event, value) =>
              field.onChange(value?.data?.buildingId ?? null)
            }
            onBlur={field.onBlur}
            getOptionLabel={getDaluxBuildingLabel}
            isOptionEqualToValue={(option, value) =>
              option?.data?.buildingId === value?.data?.buildingId
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={t('ticket.building_label')}
                fullWidth
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {buildingsLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        )}
      />

      <Controller
        name="topicId"
        control={control}
        render={({ field, fieldState }) => (
          <Autocomplete
            fullWidth
            options={topics}
            loading={topicsLoading}
            disabled={disabled}
            value={
              topics.find((option) => option?.topicId === field.value) || null
            }
            onChange={(_event, value) => field.onChange(value?.topicId ?? null)}
            onBlur={field.onBlur}
            getOptionLabel={(option) => option?.name || option?.topicId || ''}
            isOptionEqualToValue={(option, value) =>
              option?.topicId === value?.topicId
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('ticket.topic_label')}
                fullWidth
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {topicsLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        )}
      />

      <RHFTextField
        name="description"
        control={control}
        required
        label={t('ticket.description_label')}
        placeholder={t('ticket.description_placeholder')}
        multiline
        rows={4}
        fullWidth
        size="small"
        disabled={disabled}
      />

      <RHFTextField
        name="reporterName"
        control={control}
        label={t('ticket.reporter_name')}
        fullWidth
        size="small"
        disabled={disabled}
      />

      <RHFTextField
        name="reporterEmail"
        control={control}
        label={t('ticket.reporter_email')}
        helperText={t('ticket.reporter_email_helper')}
        fullWidth
        size="small"
        type="email"
        disabled
      />

      {showPhotoUpload ? (
        <Controller
          name="photo"
          control={control}
          render={({ field, fieldState }) => (
            <Stack spacing={1}>
              <input
                ref={field.ref}
                type="file"
                accept="image/*"
                hidden
                id="dalux-ticket-photo-input"
                data-testid="dalux-ticket-photo-input"
                onBlur={field.onBlur}
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  field.onChange(file);
                  event.target.value = '';
                }}
              />
              <Typography variant="body2" color="text.primary">
                {t('ticket.photo_label')}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Button
                  type="button"
                  variant="outlined"
                  disabled={disabled}
                  startIcon={<FileUploadRoundedIcon />}
                  onClick={() => {
                    const input = document.getElementById(
                      'dalux-ticket-photo-input',
                    );
                    input?.click();
                  }}
                >
                  {field.value
                    ? t('ticket.replace_photo')
                    : t('ticket.add_photo')}
                </Button>
                {field.value ? (
                  <Button
                    type="button"
                    color="error"
                    disabled={disabled}
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={() => field.onChange(null)}
                  >
                    {t('ticket.remove_photo')}
                  </Button>
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {field.value?.name || t('ticket.no_photo_selected')}
              </Typography>
              {fieldState.error?.message ? (
                <Typography variant="body2" color="error.main">
                  {fieldState.error.message}
                </Typography>
              ) : null}
            </Stack>
          )}
        />
      ) : null}
    </Stack>
  );
}
